<template>
  <section class="co-page">
    <p class="co-eyebrow">Equipa</p>
    <h1 class="co-title">Guias</h1>
    <p class="lead">Os guias criam conta pelo link do tour (Google ou email) com o nome e o WhatsApp. Só depois de aprovados veem e respondem aos tours. A avaliação nunca é mostrada aos guias.</p>

    <div v-if="loading" class="co-card loading"><b-loading :is-full-page="false" :active="true" /></div>
    <p v-else-if="error" class="co-card empty">{{ error }}</p>

    <template v-else>
      <!-- Waiting for approval -->
      <section v-if="pending.length" class="co-card pending">
        <div class="pending-head">
          <h2>Por aprovar · {{ pending.length }}</h2>
          <p>Criaram conta pelo link do grupo. Até aprovares, não veem nem respondem aos tours.</p>
        </div>
        <div v-for="g in pending" :key="g.id" class="pending-row">
          <div>
            <p class="name">{{ g.name }}</p>
            <p class="small">
              <a :href="waLink(g.phone)" target="_blank" rel="noopener" class="wa">{{ g.phone }}</a>
              <template v-if="g.email"> · {{ g.email }}</template> · registou-se {{ stamp(g.createdAt) }}
            </p>
          </div>
          <div class="row-actions">
            <button type="button" class="co-btn is-lime is-small" :disabled="busy === g.id" @click="setStatus(g, 'active')">Aprovar</button>
            <button type="button" class="co-btn is-small" :disabled="busy === g.id" @click="setStatus(g, 'inactive')">Recusar</button>
          </div>
        </div>
      </section>

      <!-- Approved before the approval email existed: send it once -->
      <section v-if="missingEmail.length" class="co-card pending">
        <div class="pending-head">
          <h2>Sem email de aprovação · {{ missingEmail.length }}</h2>
          <p>Aprovados antes de o email ser automático. Agora é enviado sozinho a cada aprovação.</p>
          <button type="button" class="co-btn is-lime is-small" :disabled="!!busy" @click="sendAllApprovalEmails">Enviar a todos</button>
        </div>
        <div v-for="g in missingEmail" :key="g.id" class="pending-row">
          <div>
            <p class="name">{{ g.name }}</p>
            <p class="small">{{ g.email || "Sem email na conta" }}</p>
          </div>
          <div class="row-actions">
            <button type="button" class="co-btn is-small" :disabled="!!busy || !g.email" @click="sendApprovalEmail(g)">Enviar email</button>
          </div>
        </div>
      </section>

      <!-- Estimate of what guides earn: tours on the team x pay per guide -->
      <section class="co-card earnings">
        <div>
          <p class="co-label">Pagamentos aos guias (estimativa)</p>
          <p class="earn-total">{{ euros(monthTotal.amount) }}</p>
          <p class="small">
            {{ plural(monthTotal.tours, "pagamento", "pagamentos") }} a guias em {{ monthLabel(month) }}
            <template v-if="monthTotal.unpriced"> · {{ plural(monthTotal.unpriced, "tour sem valor", "tours sem valor") }}</template>
          </p>
        </div>
        <label class="month-pick">
          <span class="small">Mês</span>
          <select v-model="month">
            <option v-for="m in months" :key="m" :value="m">{{ monthLabel(m) }}</option>
          </select>
        </label>
      </section>

      <div class="toolbar">
        <div class="filters">
          <button v-for="f in filters" :key="f.id" type="button" :class="{ 'is-active': filter === f.id }" @click="filter = f.id">
            {{ f.label }} · {{ f.count }}
          </button>
        </div>
        <label class="search">
          <b-icon icon="search" size="is-small" />
          <input v-model.trim="search" type="search" placeholder="Nome ou telefone" />
        </label>
      </div>

      <div class="co-card table-card">
        <p v-if="!rows.length" class="empty">Sem guias aqui.</p>
        <template v-else>
          <div class="row head-row">
            <span>Guia</span><span>Telefone</span><span>Avaliação (só admin)</span><span>{{ monthLabel(month) }}</span><span>Cancelou</span><span></span>
          </div>
          <div v-for="g in rows" :key="g.id" class="row">
            <span class="name">
              {{ g.name }}<span v-if="g.isExternal" class="ext">Externo</span>
              <span class="tg" :class="{ 'is-on': g.telegram }" :title="g.telegram ? 'Telegram ligado' : 'Ainda não ligou o Telegram'">
                <b-icon icon="paper-plane" size="is-small" />{{ g.telegram ? (g.telegram.username ? `@${g.telegram.username}` : "ligado") : "sem Telegram" }}
              </span>
            </span>
            <span class="small">
              <a v-if="g.phone" :href="waLink(g.phone)" target="_blank" rel="noopener" class="wa">{{ g.phone }}</a>
              <template v-else>Sem telefone</template>
            </span>
            <span class="stars">
              <span v-if="g.isExternal" class="small">–</span>
              <button v-else type="button" class="rating-btn" :class="{ 'is-worker': g.rating >= 5 }" @click="openRating(g)">
                <span class="rating-value">★ {{ ratingLabel(g.rating || 3) }}</span>
                <span class="small">{{ g.rating >= 5 ? "Trabalhador" : shareLabel(g.share) }}</span>
              </button>
            </span>
            <span class="num">
              {{ euros(earned(g).amount) }}
              <span class="small">{{ plural(earned(g).tours, "tour", "tours") }}</span>
            </span>
            <span class="small">{{ g.cancelledThisMonth || 0 }} este mês</span>
            <span class="row-actions">
              <button
                v-if="g.status === 'active' && !g.telegram && !g.isExternal"
                type="button"
                class="link"
                :disabled="busy === g.id"
                title="Abre o WhatsApp com o link pessoal do bot (e copia o link)"
                @click="sendTelegramInvite(g)"
              >
                Link Telegram
              </button>
              <button v-if="g.status === 'active'" type="button" class="link" :disabled="busy === g.id" @click="setStatus(g, 'inactive')">Desativar</button>
              <button v-else-if="g.status === 'inactive'" type="button" class="link" :disabled="busy === g.id" @click="setStatus(g, 'active')">Reativar</button>
            </span>
          </div>
        </template>
      </div>
    </template>

    <!-- Rating: the stars move on their own (email_sender utils/coreanosScore.js) -->
    <div v-if="ratingOf" ref="ratingOverlay" class="sheet-overlay" tabindex="-1" @click.self="ratingOf = null" @keydown.esc="ratingOf = null">
      <div class="co-card sheet" role="dialog" aria-modal="true">
        <div class="sheet-head">
          <div>
            <h2>{{ ratingOf.name }}</h2>
            <p class="small">Os guias nunca veem a avaliação.</p>
          </div>
          <button type="button" class="link" @click="ratingOf = null">Fechar</button>
        </div>

        <div class="score">
          <div>
            <span class="score-value">★ {{ ratingLabel(ratingOf.rating || 3) }}</span>
            <span class="small">{{
              ratingOf.rating >= 5 ? "Trabalhador · não muda sozinho" : `Ponto de partida: ★ ${ratingLabel(history ? history.base : ratingOf.baseRating || 3)}`
            }}</span>
          </div>
          <div v-if="ratingOf.rating < 5 || !ratingOf.rating">
            <span class="score-value">{{ ratingOf.share ? `${ratingOf.share.tours}/${ratingOf.share.available}` : "–" }}</span>
            <span class="small">tours / vezes disponível (30 dias)</span>
          </div>
        </div>

        <p class="co-label">Definir à mão</p>
        <div class="stars">
          <button
            v-for="n in 5"
            :key="n"
            type="button"
            :class="{ 'is-on': (ratingOf.rating >= 5 ? 5 : Math.min(4, Math.round(ratingOf.rating || 3))) === n }"
            :disabled="busy === ratingOf.id"
            @click="rate(ratingOf, n)"
          >
            {{ n }}
          </button>
        </div>
        <p class="small hint">5 = os nossos trabalhadores: prioridade sempre e a nota não muda sozinha. Uma nota à mão passa a ser o novo ponto de partida.</p>

        <p class="co-label">Histórico</p>
        <p v-if="!history" class="small">A carregar…</p>
        <p v-else-if="!history.events.length" class="small">Ainda sem registos.</p>
        <ul v-else class="events">
          <li v-for="e in history.events" :key="e.id" :class="{ 'is-off': e.justified || !e.counts }">
            <div class="event-main">
              <span class="event-label">{{ eventText(e) }}</span>
              <span class="small">{{ e.tour || stamp(e.at) }}<template v-if="!e.counts"> · antes da nota à mão</template></span>
            </div>
            <span v-if="e.delta || e.justified" class="delta" :class="{ 'is-bad': e.delta < 0 }">{{ deltaLabel(e) }}</span>
            <button v-if="e.counts && (e.delta < 0 || e.justified)" type="button" class="link" :disabled="busy === ratingOf.id" @click="justify(e)">
              {{ e.justified ? "Voltar a contar" : "Justificar" }}
            </button>
          </li>
        </ul>
      </div>
    </div>
  </section>
</template>


<script>
import {
  listTukGuides,
  setTukGuideStatus,
  setTukGuideRating,
  getTukGuideEvents,
  justifyTukGuideEvent,
  ratingLabel,
  sendTukGuideApprovalEmail,
  getGuideTelegramInvite,
  stamp,
  waLink,
  plural,
} from "@/services/coreanos";

const MONTHS = ["janeiro", "fevereiro", "março", "abril", "maio", "junho", "julho", "agosto", "setembro", "outubro", "novembro", "dezembro"];
const thisMonth = () => new Intl.DateTimeFormat("en-CA", { timeZone: "Europe/Lisbon" }).format(new Date()).slice(0, 7);

export default {
  name: "TukGuides",
  metaInfo: { title: "Guias | Pedidos Coreanos" },
  data() {
    return { guides: [], loading: true, error: "", filter: "active", search: "", busy: "", month: thisMonth(), ratingOf: null, history: null };
  },
  computed: {
    // This month and the 5 before, plus any month with tours (e.g. next month)
    months() {
      const set = new Set();
      const [y, m] = thisMonth().split("-").map(Number);
      for (let i = 0; i < 6; i++) {
        const d = new Date(Date.UTC(y, m - 1 - i, 1));
        set.add(`${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}`);
      }
      this.guides.forEach((g) => Object.keys(g.earnings || {}).forEach((k) => set.add(k)));
      return [...set].sort().reverse();
    },
    monthTotal() {
      return this.guides.reduce(
        (sum, g) => {
          const e = this.earned(g);
          return { amount: sum.amount + e.amount, tours: sum.tours + e.tours, unpriced: sum.unpriced + e.unpriced };
        },
        { amount: 0, tours: 0, unpriced: 0 },
      );
    },
    pending() {
      return this.guides.filter((g) => g.status === "pending");
    },
    missingEmail() {
      return this.guides.filter((g) => g.status === "active" && !g.isExternal && !g.approvalEmailSent);
    },
    groups() {
      return {
        active: this.guides.filter((g) => g.status === "active" && !g.isExternal),
        external: this.guides.filter((g) => g.isExternal && g.status !== "inactive"),
        inactive: this.guides.filter((g) => g.status === "inactive"),
      };
    },
    filters() {
      return [
        { id: "active", label: "Ativos", count: this.groups.active.length },
        { id: "external", label: "Externos", count: this.groups.external.length },
        { id: "inactive", label: "Inativos", count: this.groups.inactive.length },
      ];
    },
    rows() {
      const q = this.search.toLowerCase().replace(/\s/g, "");
      return this.groups[this.filter].filter((g) => !q || `${g.name}${g.phone}`.toLowerCase().replace(/\s/g, "").includes(q));
    },
  },
  mounted() {
    this.load();
  },
  methods: {
    stamp,
    waLink,
    plural,
    ratingLabel,
    // "2/5 tours" = tours got / times available in the last 30 days
    shareLabel(share) {
      return share && share.available ? `${share.tours}/${share.available} tours (30 dias)` : "sem respostas (30 dias)";
    },
    eventText(e) {
      if (e.type === "dropout") return `${e.label}${e.hoursBefore != null ? ` · ${Math.max(0, Math.round(e.hoursBefore))} h antes` : ""}${e.byAdmin ? " (marcado pelo admin)" : ""}`;
      if (e.type === "late") return `${e.label} · ${e.minutes} min`;
      if (e.type === "swap") return `${e.label}${e.with ? ` (${e.with})` : ""}`;
      return e.label;
    },
    // "−0,3", and once it has faded "−0,3 (agora −0,15)"
    deltaLabel(e) {
      if (e.justified) return "justificado";
      const n = (v) => `${v > 0 ? "+" : "−"}${Math.abs(v).toLocaleString("pt-PT", { maximumFractionDigits: 2 })}`;
      const now = Math.round(e.delta * e.weight * 100) / 100;
      return e.weight < 0.95 && now !== e.delta ? `${n(e.delta)} (agora ${n(now)})` : n(e.delta);
    },
    async openRating(guide) {
      this.ratingOf = guide;
      this.history = null;
      this.$nextTick(() => this.$refs.ratingOverlay && this.$refs.ratingOverlay.focus());
      await this.loadHistory();
    },
    async loadHistory() {
      const guide = this.ratingOf;
      try {
        const history = await getTukGuideEvents(guide.id);
        if (this.ratingOf !== guide) return;
        this.history = history;
        guide.rating = history.rating;
        guide.share = history.share;
      } catch (error) {
        this.toast(error.message, "is-danger");
      }
    },
    async justify(e) {
      this.busy = this.ratingOf.id;
      try {
        await justifyTukGuideEvent(this.ratingOf.id, e.id, !e.justified);
        await this.loadHistory();
      } catch (error) {
        this.toast(error.message, "is-danger");
      } finally {
        this.busy = "";
      }
    },
    earned(g) {
      return (g.earnings || {})[this.month] || { amount: 0, tours: 0, unpriced: 0 };
    },
    euros(v) {
      return `${Number(v || 0).toLocaleString("pt-PT", { maximumFractionDigits: 2 })} €`;
    },
    monthLabel(key) {
      const [y, m] = key.split("-").map(Number);
      return `${MONTHS[m - 1]} ${y}`;
    },
    async load() {
      try {
        this.guides = await listTukGuides();
      } catch (error) {
        this.error = error.message;
      } finally {
        this.loading = false;
      }
    },
    async setStatus(guide, status) {
      this.busy = guide.id;
      try {
        const result = await setTukGuideStatus(guide.id, status);
        guide.status = status;
        if (result.emailSent) {
          guide.approvalEmailSent = true;
          this.toast(`${guide.name} aprovado. Email de aprovação enviado.`, "is-success");
        } else if (result.emailError) {
          this.toast(`${guide.name} aprovado, mas o email falhou: ${result.emailError}`, "is-danger");
        }
      } catch (error) {
        this.$buefy.toast.open({ message: error.message, type: "is-danger", position: "is-bottom" });
      } finally {
        this.busy = "";
      }
    },
    toast(message, type) {
      this.$buefy.toast.open({ message, type, duration: 6000, position: "is-bottom" });
    },
    async sendApprovalEmail(guide, quiet = false) {
      this.busy = guide.id;
      try {
        await sendTukGuideApprovalEmail(guide.id);
        guide.approvalEmailSent = true;
        if (!quiet) this.toast(`Email de aprovação enviado a ${guide.name}.`, "is-success");
        return true;
      } catch (error) {
        this.toast(`${guide.name}: ${error.message}`, "is-danger");
        return false;
      } finally {
        this.busy = "";
      }
    },
    // Personal link to the bot: one tap links the account (send before launch)
    async sendTelegramInvite(guide) {
      const tab = window.open("", "_blank");
      this.busy = guide.id;
      try {
        const { url } = await getGuideTelegramInvite(guide.id);
        const text = `Olá ${String(guide.name || "").split(" ")[0]}! A partir de agora respondes aos tours Coreanos diretamente no Telegram. Abre este link e carrega em «Começar» para ligar a tua conta: ${url}`;
        try {
          await navigator.clipboard.writeText(url);
        } catch (e) {
          // no clipboard access: the WhatsApp message has the link
        }
        const wa = `${waLink(guide.phone)}?text=${encodeURIComponent(text)}`;
        if (tab) tab.location = wa;
        else window.location.href = wa;
        this.toast(`Link de ${guide.name} copiado e WhatsApp aberto.`, "is-success");
      } catch (error) {
        if (tab) tab.close();
        this.toast(error.message, "is-danger");
      } finally {
        this.busy = "";
      }
    },
    async sendAllApprovalEmails() {
      let sent = 0;
      for (const g of this.missingEmail.filter((x) => x.email)) {
        if (await this.sendApprovalEmail(g, true)) sent++;
      }
      this.toast(`${sent} ${sent === 1 ? "email enviado" : "emails enviados"}.`, "is-success");
    },
    async rate(guide, rating) {
      const before = guide.rating;
      guide.rating = rating;
      this.busy = guide.id;
      try {
        const result = await setTukGuideRating(guide.id, rating);
        guide.rating = result.rating;
        if (this.ratingOf === guide) await this.loadHistory();
      } catch (error) {
        guide.rating = before;
        this.$buefy.toast.open({ message: error.message, type: "is-danger", position: "is-bottom" });
      } finally {
        this.busy = "";
      }
    },
  },
};
</script>

<style scoped lang="scss">
@import "./coreanos";

.lead {
  margin: 0.5rem 0 1.5rem;
  color: $ink-sub;
}

.loading {
  position: relative;
  min-height: 12rem;
}

.empty {
  padding: 2rem;
  color: $ink-sub;
}

.pending {
  overflow: hidden;
  margin-bottom: 1.5rem;
}

.pending-head {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem 1rem;
  padding: 1rem 1.375rem;
  background: $warn-soft;
  color: $warn-ink;

  h2 {
    font-family: $display;
    font-size: 1.25rem;
    font-weight: 600;
  }

  p {
    font-size: 0.875rem;
  }
}

.pending-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 1rem 1.375rem;
  border-top: 1px solid $line;
}

.name {
  font-family: $display;
  font-weight: 600;
  color: $forest;
}

.small {
  font-size: 0.875rem;
  color: $ink-sub;
}

.row-actions {
  display: flex;
  justify-content: flex-start;
  gap: 0.5rem;
}

.earnings {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1.5rem;
  padding: 1.25rem 1.375rem;
}

.earn-total {
  font-family: $display;
  font-size: 2rem;
  font-weight: 600;
  color: $forest;
}

.month-pick {
  display: grid;
  gap: 0.25rem;

  select {
    height: 2.5rem;
    padding: 0 0.75rem;
    border: 1px solid $line;
    border-radius: 10px;
    background: #fff;
    font: inherit;
  }
}

.num .small {
  display: block;
  font-family: inherit;
  font-weight: 400;
}

.toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  margin-bottom: 1rem;
}

.filters {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;

  button {
    @include pill-button;
    min-height: 2.5rem;
    padding: 0 1rem;
    border: 1px solid $line;
    background: #fff;
    font-size: 0.9375rem;
    color: $forest;

    &.is-active {
      border-color: $forest;
      background: $forest;
      color: #fff;
    }
  }
}

.search {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  width: 18rem;
  max-width: 100%;
  height: 2.75rem;
  padding: 0 1rem;
  border: 1px solid $line;
  border-radius: 999px;
  background: #fff;
  color: $ink-faint;

  input {
    flex: 1;
    min-width: 0;
    border: 0;
    outline: 0;
    font: inherit;
    color: $ink;
  }
}

.table-card {
  overflow: hidden;
}

.row {
  display: grid;
  grid-template-columns: minmax(0, 1.4fr) minmax(0, 1fr) 13rem 6rem 7rem 6rem;
  gap: 1rem;
  align-items: center;
  padding: 0.875rem 1.375rem;
  border-top: 1px solid $line;

  &.head-row {
    border-top: 0;
    background: #f6f9f6;
    font-family: $display;
    font-size: 0.75rem;
    font-weight: 500;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: $ink-sub;
  }

  @media (max-width: 900px) {
    grid-template-columns: 1fr auto;

    &.head-row {
      display: none;
    }

    .stars {
      grid-column: 1 / -1;
    }
  }
}

.wa {
  color: $forest-2;
  text-decoration: underline;
}

.tg {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  margin-left: 0.5rem;
  font-family: inherit;
  font-size: 0.75rem;
  font-weight: 400;
  color: $ink-faint;

  &.is-on {
    color: $forest-2;
  }
}

.ext {
  margin-left: 0.5rem;
  padding: 0.125rem 0.5rem;
  border-radius: 999px;
  background: $warn-bg;
  font-size: 0.75rem;
  color: $warn-ink;
}

.stars {
  display: flex;
  gap: 0.375rem;

  button {
    width: 2.25rem;
    height: 2.25rem;
    border: 1px solid $line;
    border-radius: 8px;
    background: #fff;
    font-family: $display;
    font-weight: 600;
    color: $ink-sub;
    cursor: pointer;

    &.is-on {
      border-color: $forest;
      background: $forest;
      color: $lime;
    }
  }
}

.num {
  font-family: $display;
  font-weight: 600;
  color: $forest;
}

.link {
  padding: 0;
  border: 0;
  background: none;
  font: inherit;
  font-size: 0.875rem;
  font-weight: 500;
  color: $forest;
  text-decoration: underline;
  cursor: pointer;
}

.rating-btn {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 0.25rem 0.625rem;
  border: 1px solid $line;
  border-radius: 10px;
  background: #fff;
  font: inherit;
  text-align: left;
  cursor: pointer;

  &:hover {
    border-color: $forest;
  }

  &.is-worker {
    border-color: $forest;
    background: $forest;

    .rating-value {
      color: $lime;
    }

    .small {
      color: rgba(255, 255, 255, 0.8);
    }
  }
}

.rating-value {
  font-family: $display;
  font-weight: 700;
  color: $forest;
}

// Own overlay (not b-modal), as in CoreanosList; a bottom sheet on phones
.sheet-overlay {
  position: fixed;
  inset: 0;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  background: rgba(10, 30, 25, 0.55);
  outline: 0;

  @media (max-width: 768px) {
    align-items: flex-end;
    padding: 0;
  }
}

.sheet {
  width: 100%;
  max-width: 34rem;
  max-height: calc(100vh - 2rem);
  overflow-y: auto;
  padding: 1.5rem;
  background: #fff;

  h2 {
    font-family: $display;
    font-size: 1.25rem;
    font-weight: 600;
    color: $forest;
  }

  .co-label {
    margin: 1.25rem 0 0.5rem;
  }

  @media (max-width: 768px) {
    max-width: none;
    max-height: 85vh;
    border-radius: 20px 20px 0 0;
    padding-bottom: calc(1.5rem + env(safe-area-inset-bottom));
  }
}

.sheet-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
}

.score {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.5rem;
  margin-top: 1rem;

  > div {
    display: flex;
    flex-direction: column;
    padding: 0.75rem 1rem;
    border: 1px solid $line;
    border-radius: 12px;
  }
}

.score-value {
  font-family: $display;
  font-size: 1.5rem;
  font-weight: 700;
  color: $forest;
}

.hint {
  margin-top: 0.5rem;
}

.events {
  margin: 0;
  padding: 0;
  list-style: none;

  li {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.25rem 0.75rem;
    padding: 0.625rem 0;
    border-top: 1px solid $line;

    &.is-off {
      opacity: 0.55;
    }
  }
}

.event-main {
  display: flex;
  flex: 1;
  flex-direction: column;
  min-width: 10rem;
}

.event-label {
  font-size: 0.9375rem;
  color: $ink;
}

.delta {
  font-family: $display;
  font-size: 0.875rem;
  font-weight: 600;
  color: $forest-2;

  &.is-bad {
    color: #a32c1f;
  }
}
</style>
